import type { CSSProperties, ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import BetaBanner from "./BetaBanner";
import AppTabBar from "./AppTabBar";

// Wraps every /app/* page with: top bar (back arrow if not on /app, italic
// Newsreader title, optional right action), the sticky Beta banner, the
// content slot, and a sticky bottom tab bar. Mobile-first 420 px column,
// widens to 480 px max on desktop on a paper background filling the viewport.

export default function AppShell({
  title,
  eyebrow,
  action,
  children,
  hideBack,
  backTo,
  contentStyle,
}: {
  title: ReactNode;
  eyebrow?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  hideBack?: boolean;
  backTo?: string;
  contentStyle?: CSSProperties;
}) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isAppRoot = pathname === "/app" || pathname === "/app/";
  const showBack = !hideBack && !isAppRoot;

  return (
    <div
      className="paper-bg"
      style={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 480,
          minHeight: "100vh",
          background: "var(--paper-100)",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          boxShadow: "0 0 0 1px rgba(43,26,15,0.04)",
        }}
      >
        <BetaBanner />

        {/* Top bar */}
        <div
          style={{
            padding: "20px 22px 0",
            display: "flex",
            alignItems: "center",
            gap: 12,
            minHeight: 52,
          }}
        >
          {showBack ? (
            <button
              type="button"
              onClick={() => (backTo ? navigate(backTo) : navigate(-1))}
              aria-label="Zurück"
              style={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                background: "var(--paper-50)",
                border: "1px solid rgba(43,26,15,0.18)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                cursor: "pointer",
                padding: 0,
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="var(--ink-soft)"
                strokeWidth="2"
                aria-hidden
              >
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>
          ) : (
            <Link
              to="/app"
              style={{
                fontFamily: "var(--f-display)",
                fontSize: 20,
                fontStyle: "italic",
                color: "var(--ink)",
                textDecoration: "none",
              }}
            >
              Vitanara<span style={{ color: "var(--oxblood)" }}>.</span>
            </Link>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            {eyebrow ? (
              <div
                className="t-caps"
                style={{
                  fontSize: 10,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {eyebrow}
              </div>
            ) : null}
          </div>
          {action}
        </div>

        {/* Title */}
        <div style={{ padding: "14px 22px 0" }}>
          <h1
            style={{
              fontFamily: "var(--f-display)",
              fontWeight: 400,
              fontSize: 30,
              lineHeight: 1.05,
              letterSpacing: "-0.015em",
              margin: "0 0 16px",
              color: "var(--ink)",
              fontStyle: "italic",
            }}
          >
            {title}
          </h1>
        </div>

        {/* Content */}
        <div style={{ flex: 1, ...contentStyle }}>{children}</div>

        <AppTabBar />
      </div>
    </div>
  );
}
